import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import { useChartTheme } from '@/components/charts/theme';
import { type FeedingDashboardConfig, type FeedingPen, penLabel } from '@/domain/feedingDashboard';

import { pelletSurfaceQuality } from './bioscopeQuality';
import { dashboardLocale } from './dashboardLocale';
import { FeedingChartPanel } from './FeedingChartPanel/FeedingChartPanel';
import { FEEDING_COLORS, WARNED_BAR_OUTLINE_WIDTH, warningPalette } from './feedingChartColors';
import { PenChartKey } from './PenChartKey';
import {
    fallthroughColor,
    fallthroughOutline,
    fallthroughThresholdLines,
    isPelletBanded,
} from './pelletBandColors';

interface FallthroughPerPenChartProps {
    pens: FeedingPen[];
    /**
     * Unique label per pen id (`penDisplayNames` over the period's pens). Bars are keyed by
     * it, so two pens sharing a name would otherwise both draw the second's value.
     */
    penNames?: ReadonlyMap<number, string>;
    config: FeedingDashboardConfig;
    loading?: boolean;
    error?: unknown;
}

/** Average pellet fallthrough per pen for the selected period (IPS 28, 2.7.3). */
export function FallthroughPerPenChart(props: FallthroughPerPenChartProps) {
    const { pens, penNames, config, loading, error } = props;
    const { t, i18n } = useTranslation();
    const { fallthroughWarnPct: warnPct, fallthroughAlertPct: alertPct } = config;
    // Re-derived when the theme switches; the outline ink differs between themes.
    const chartTheme = useChartTheme();
    const palette = useMemo(() => warningPalette(chartTheme), [chartTheme]);

    // A pen without a feeding camera can never have a bar, so on a mixed site its slot
    // would be a permanent hole that reads as missing data.
    const cameraPens = pens.filter((pen) => pen.fallthroughSupported);
    // The same rule the table cell and the KPI card apply: pellet availability, not
    // the weight startup window, which says nothing about pellet counts.
    const bars = cameraPens.map((pen) => ({
        pen,
        label: penLabel(penNames, pen),
        value: pen.fallthroughAvailable ? pen.fallthroughPct : null,
    }));
    const hasValue = bars.some(({ value }) => value != null);
    const quality = pelletSurfaceQuality(hasValue);
    const banded = bars.some(({ pen, value }) =>
        isPelletBanded(value, pen.config.fallthroughWarnPct, pen.config.fallthroughAlertPct)
    );

    const spec: MetricChartSpec<'bar'> = {
        kind: 'bar',
        xAxisKind: 'category',
        days: bars.map(({ label }) => label),
        locale: dashboardLocale(i18n),
        series: [
            {
                key: 'fallthrough',
                label: t('feedingModule.kpi.fallthrough'),
                color: FEEDING_COLORS.bioscope,
                // The only series on the chart. useMetricChart refuses to hide the last
                // series carrying a legend entry, so a legend here renders a control
                // that does nothing when clicked. The axis title already names it.
                showInLegend: false,
                data: bars.map(({ label, value }) => ({ day: label, value })),
                // Each pen against its OWN thresholds; the reference lines stay the site
                // values, since one line per bar would be unreadable.
                barColors: bars.map(({ pen, value }) =>
                    fallthroughColor(
                        value,
                        pen.config.fallthroughWarnPct,
                        pen.config.fallthroughAlertPct,
                        palette
                    )
                ),
                barOutlines: {
                    colors: bars.map(({ pen, value }) =>
                        fallthroughOutline(
                            value,
                            pen.config.fallthroughWarnPct,
                            pen.config.fallthroughAlertPct,
                            palette
                        )
                    ),
                    width: WARNED_BAR_OUTLINE_WIDTH,
                },
                decimals: 1,
            },
        ],
        left: {
            label: t('feedingModule.kpi.fallthrough'),
            unit: '%',
            beginAtZero: true,
            valueDecimals: 1,
        },
        referenceLines: fallthroughThresholdLines(warnPct, alertPct, palette),
    };

    const chart = useMetricChart(spec);
    const title = t('feedingModule.charts.fallthroughPerPenTitle');

    return (
        <FeedingChartPanel title={title} quality={quality} qualitySubject="pellet">
            <MetricChart
                kind="bar"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                isLoading={loading}
                error={error}
                // Its own short text: beside the timeline's empty state, the same
                // sentences twice read as one message printed by mistake.
                noDataMessage={t('feedingModule.charts.fallthroughPerPenNoData')}
            />
            {hasValue ? (
                <PenChartKey
                    kind="pellet"
                    warnPct={warnPct}
                    alertPct={alertPct}
                    palette={palette}
                    banded={banded}
                />
            ) : null}
        </FeedingChartPanel>
    );
}
